// ViewerCore — instanční, na UI nezávislé jádro nad @thatopen/components + web-ifc.
// Na rozdíl od standalone jádra aplikace (src/core.js) nemá žádnou vazbu na konkrétní
// DOM ani globální singleton: vše drží instance, takže lze mít víc viewerů na stránce
// a interní knihovny zůstávají skryté před hostitelskou aplikací (§24 zadání).
//
// ViewerCore záměrně NEobsahuje žádné veřejné API — to poskytuje IFCViewer (fasáda).
// Zde je jen technické jádro: scéna, renderer, kamera, načtení IFC, federační offset,
// ghosting a raycast. Mirroruje ověřený init z aplikace, ale odděleně (§2, §35/#4).
import * as OBC from "@thatopen/components";
import * as OBCF from "@thatopen/components-front";
import * as THREE from "three";
import { sceneToIfc as coordsSceneToIfc, ifcToScene as coordsIfcToScene } from "./coords.js";
import { LineSegments2 } from "three/examples/jsm/lines/LineSegments2.js";
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry.js";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";

const OUTLINE_WIDTH = 2;   // tloušťka hran výběru v px (2× výchozí 1px čára)

const STRUCT = [
  "IFCWALL", "IFCWALLSTANDARDCASE", "IFCSLAB", "IFCROOF", "IFCCOLUMN", "IFCBEAM",
  "IFCFOOTING", "IFCPILE", "IFCSTAIR", "IFCSTAIRFLIGHT", "IFCRAMP", "IFCRAMPFLIGHT",
  "IFCMEMBER", "IFCPLATE", "IFCCURTAINWALL", "IFCCOVERING",
];

function bgColor(background) {
  if (background && typeof background === "object" && "r" in background) return new THREE.Color(background.r, background.g, background.b);
  if (typeof background === "number") return new THREE.Color(background);
  if (background === "light") return new THREE.Color(0xf1f3f6);
  if (typeof background === "string" && background[0] === "#") return new THREE.Color(background);
  return new THREE.Color(0x1b1e24); // "dark" (výchozí)
}

export class ViewerCore {
  constructor() {
    this.components = null; this.world = null;
    this.classifier = null; this.hider = null; this.indexer = null;
    this.ifcLoader = null; this.highlighter = null;
    this.container = null; this.gridMesh = null;
    this.federationOffset = new THREE.Vector3();
    this.federationOffsetInit = false;
    this._ghostSwapped = [];
    this._selOutline = null;
    this._rcaster = new THREE.Raycaster();
    this._rcam = new THREE.PerspectiveCamera();
  }

  async init(container, opts = {}) {
    this.container = container;
    this.components = new OBC.Components();
    const worlds = this.components.get(OBC.Worlds);
    this.world = worlds.create();

    this.world.scene = new OBC.SimpleScene(this.components);
    this.world.renderer = new OBC.SimpleRenderer(this.components, container);
    this.world.camera = new OBC.SimpleCamera(this.components);

    this.components.init();

    this.world.scene.setup();

    if (opts.grid !== false) {
      const grids = this.components.get(OBC.Grids);
      const grid = grids.create(this.world);
      this._grid = grid;
      // Zapamatovat výchozí (tmavou) barvu mřížky pro přepínání motivu.
      this._gridDarkColor = (grid.config && grid.config.color && grid.config.color.clone)
        ? grid.config.color.clone() : new THREE.Color(0x3a3f47);
      this.gridMesh = (grid && grid.three) ? grid.three : null;
    }

    // Barevné schéma (světlé/tmavé) — nastaví pozadí i barvu mřížky.
    this.setTheme(opts.background != null ? opts.background : "dark");

    this.ifcLoader = this.components.get(OBC.IfcLoader);
    await this.ifcLoader.setup({ autoSetWasm: false });
    // Cesta k web-ifc.wasm — plně konfigurovatelná hostitelem (offline/same-origin).
    this.ifcLoader.settings.wasm = opts.wasm || { path: "", absolute: false };

    this.classifier = this.components.get(OBC.Classifier);
    this.hider = this.components.get(OBC.Hider);
    this.indexer = this.components.get(OBC.IfcRelationsIndexer);

    this.highlighter = this.components.get(OBCF.Highlighter);
    this.highlighter.setup({ world: this.world, selectionColor: null });

    // Udržet tloušťku hran výběru správnou i po změně velikosti okna.
    this._onResize = () => this.refreshOutlineResolution();
    if (typeof window !== "undefined") window.addEventListener("resize", this._onResize);
    return this;
  }

  // Barevné schéma: "light" | "dark" | hex | {r,g,b}. Nastaví pozadí scény i barvu
  // mřížky (na světlém pozadí světle šedá, na tmavém výchozí). Lze volat za běhu.
  setTheme(theme) {
    const bg = bgColor(theme);
    if (this.world && this.world.scene) this.world.scene.three.background = bg;
    this.isLight = (0.299 * bg.r + 0.587 * bg.g + 0.114 * bg.b) > 0.6;
    this.theme = this.isLight ? "light" : "dark";
    if (this._grid && this._grid.config) {
      try { this._grid.config.color = this.isLight ? new THREE.Color(0xb4bac4) : (this._gridDarkColor || new THREE.Color(0x3a3f47)); } catch (_) {}
    }
    return this;
  }

  // Načte IFC z bajtů a zapojí do scény (klasifikace, index vztahů, průhlednost
  // místností, federační offset shodný pro všechny modely).
  async loadGroup(bytes, opts = {}) {
    const group = await this.ifcLoader.load(bytes);
    this.world.scene.three.add(group);
    group.updateMatrixWorld(true);

    if (!this.federationOffsetInit) {
      const b = new THREE.Box3().setFromObject(group);
      if (!b.isEmpty()) { this.federationOffset.copy(b.min).multiplyScalar(-1); this.federationOffsetInit = true; }
    }
    group.position.add(this.federationOffset);
    group.updateMatrixWorld(true);

    this.classifier.byEntity(group);
    const so = Number(opts.spaceOpacity);
    this._setTransparency(["IFCSPACE"], Number.isFinite(so) ? Math.min(1, Math.max(0, so)) : 0.5);
    this._snapshotOriginals();
    try { await this.indexer.process(group); } catch (_) { /* psety pak nemusí být */ }
    return group;
  }

  disposeGroup(group) {
    try { this.world.scene.three.remove(group); } catch (_) {}
    const frags = this.components.get(OBC.FragmentsManager);
    try { frags.disposeGroup(group); } catch (_) { try { group.removeFromParent(); } catch (_) {} }
  }

  // --- Kamera ---------------------------------------------------------------
  fitToBox(box, animate = true) {
    try {
      if (!box || box.isEmpty() || !this.world.camera.controls) return;
      const sphere = box.getBoundingSphere(new THREE.Sphere());
      if (sphere.radius > 0) this.world.camera.controls.fitToSphere(sphere, animate);
    } catch (_) { /* fit je best-effort */ }
  }

  boxOfGroups(groups) {
    const box = new THREE.Box3(); let any = false;
    for (const g of groups) {
      if (!g || !g.visible) continue;
      const b = new THREE.Box3().setFromObject(g);
      if (!b.isEmpty()) { box.union(b); any = true; }
    }
    return any ? box : null;
  }

  // --- Souřadnice IFC (Z nahoru) <-> scéna (Y nahoru) ------------------------
  // Rámec federace = rámec prvního modelu po COORDINATE_TO_ORIGIN (viz coords.js).
  _baseCoordinationMatrix() {
    try { return this.components.get(OBC.FragmentsManager).baseCoordinationMatrix || null; }
    catch (_) { return null; }
  }
  sceneToIfc(p) {
    return coordsSceneToIfc(p, this._baseCoordinationMatrix(), this.federationOffset);
  }
  ifcToScene(p) {
    return coordsIfcToScene(p, this._baseCoordinationMatrix(), this.federationOffset);
  }

  // --- Snímek pohledu (PNG) -------------------------------------------------
  // Vykreslí scénu do render targetu s kamerou synchronizovanou z camera-controls
  // (world.camera.three má občas zastaralou pozici) a přečte pixely → PNG Blob.
  async captureSnapshot(opts = {}) {
    const maxSize = opts.maxSize || 1280;    // omezení rozměru snímku (velikost/BCF, §38/§39)
    const renderer = this.world.renderer.three;
    const scene = this.world.scene.three;
    const camThree = this.world.camera.three;
    const size = renderer.getSize(new THREE.Vector2());
    const dpr = renderer.getPixelRatio ? renderer.getPixelRatio() : 1;
    const w = Math.max(1, Math.floor(size.x * dpr));
    const h = Math.max(1, Math.floor(size.y * dpr));

    const rcam = camThree.clone();
    try {
      const cc = this.world.camera.controls;
      const p = new THREE.Vector3(), t = new THREE.Vector3();
      cc.getPosition(p); cc.getTarget(t);
      rcam.position.copy(p); rcam.up.copy(camThree.up); rcam.lookAt(t);
    } catch (_) {}
    if (rcam.isPerspectiveCamera) { rcam.aspect = w / h; }
    rcam.updateProjectionMatrix && rcam.updateProjectionMatrix();
    rcam.updateMatrixWorld(true);

    const rt = new THREE.WebGLRenderTarget(w, h);
    const prev = renderer.getRenderTarget();
    renderer.setRenderTarget(rt);
    renderer.render(scene, rcam);
    const buf = new Uint8Array(w * h * 4);
    renderer.readRenderTargetPixels(rt, 0, 0, w, h, buf);
    renderer.setRenderTarget(prev);
    rt.dispose();

    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    const img = ctx.createImageData(w, h);
    for (let y = 0; y < h; y++) {          // WebGL má počátek vlevo dole → převrátit
      const s = (h - 1 - y) * w * 4, d = y * w * 4;
      img.data.set(buf.subarray(s, s + w * 4), d);
    }
    ctx.putImageData(img, 0, 0);

    // Zmenšit, pokud přesahuje maxSize (delší strana), pro rozumnou velikost PNG.
    let outCanvas = canvas;
    const longest = Math.max(w, h);
    if (longest > maxSize) {
      const s = maxSize / longest;
      const sc = document.createElement("canvas");
      sc.width = Math.round(w * s); sc.height = Math.round(h * s);
      sc.getContext("2d").drawImage(canvas, 0, 0, sc.width, sc.height);
      outCanvas = sc;
    }
    return await new Promise((res) => outCanvas.toBlob((b) => res(b), "image/png"));
  }

  // --- Ghosting (pro izolaci/výběr) -----------------------------------------
  _eachMaterial(cb) {
    const fragments = this.components.get(OBC.FragmentsManager);
    for (const [fragID, frag] of fragments.list) {
      if (!frag || !frag.mesh) continue;
      for (const m of frag.mesh.material) { if (m) cb(m, fragID); }
    }
  }
  _snapshotOriginals() {
    this._eachMaterial((m) => {
      if (!m.userData.__orig) m.userData.__orig = { opacity: m.opacity, transparent: m.transparent, depthWrite: m.depthWrite };
    });
  }
  _setTransparency(entities, opacity) {
    const map = this.classifier.find({ entities });
    if (!map) return;
    const fragments = this.components.get(OBC.FragmentsManager);
    for (const fragID in map) {
      const frag = fragments.list.get(fragID);
      if (!frag || !frag.mesh) continue;
      for (const m of frag.mesh.material) {
        if (!m) continue;
        m.transparent = true; m.opacity = opacity; m.depthWrite = false; m.needsUpdate = true;
      }
    }
  }
  applyGhost(keepFragIds) {
    this.clearGhost();
    const fragments = this.components.get(OBC.FragmentsManager);
    this._eachMaterial((m) => { m.transparent = true; m.opacity = 0.16; m.depthWrite = false; m.needsUpdate = true; });
    for (const [fragID, frag] of fragments.list) {
      if (!frag || !frag.mesh || !keepFragIds.has(fragID)) continue;
      const orig = frag.mesh.material;
      const clones = orig.map((m) => { const c = m.clone(); c.opacity = 1; c.transparent = false; c.depthWrite = true; c.needsUpdate = true; return c; });
      frag.mesh.material = clones;
      this._ghostSwapped.push({ mesh: frag.mesh, orig });
    }
  }
  clearGhost() {
    for (const s of this._ghostSwapped) {
      for (const c of s.mesh.material) { if (c && c.dispose) c.dispose(); }
      s.mesh.material = s.orig;
    }
    this._ghostSwapped = [];
    this._eachMaterial((m) => {
      const o = m.userData.__orig;
      if (o) { m.opacity = o.opacity; m.transparent = o.transparent; m.depthWrite = o.depthWrite; m.needsUpdate = true; }
    });
  }

  // --- Magenta zvýraznění hran výběru --------------------------------------
  // Vykreslí hrany (EdgesGeometry) vybraných prvků jako čáry přes geometrii
  // (depthTest vypnutý), stejně jako standalone aplikace. `fragMaps` je pole
  // FragmentIdMap (per model). Barva výchozí magenta.
  setSelectionOutline(fragMaps, color = 0xff00ff) {
    this.clearSelectionOutline();
    const fragments = this.components.get(OBC.FragmentsManager);
    const container = new THREE.Group();
    // Fat-line materiál — tloušťku v px (na rozdíl od LineBasicMaterial, kterou
    // WebGL ignoruje). Rozlišení v CSS px, aby linewidth odpovídal pixelům.
    const mat = new LineMaterial({ color, linewidth: OUTLINE_WIDTH, worldUnits: false, depthTest: false, transparent: true });
    this._setOutlineResolution(mat);
    const m = new THREE.Matrix4();
    const geoCache = new Map();   // sdílená LineSegmentsGeometry na geometrii fragmentu
    for (const fragMap of fragMaps) {
      for (const fragID in fragMap) {
        const frag = fragments.list.get(fragID);
        if (!frag || !frag.mesh || !frag.mesh.geometry) continue;
        const mesh = frag.mesh;
        let lsg = geoCache.get(mesh.geometry);
        if (!lsg) {
          const edges = new THREE.EdgesGeometry(mesh.geometry, 20);
          lsg = new LineSegmentsGeometry();
          lsg.setPositions(edges.attributes.position.array);
          edges.dispose();
          geoCache.set(mesh.geometry, lsg);
        }
        mesh.updateWorldMatrix(true, false);
        for (const eid of fragMap[fragID]) {
          const inst = frag.getInstancesIDs(eid);
          if (!inst || inst.size === 0) continue;
          for (const idx of inst) {
            mesh.getMatrixAt(idx, m);
            const seg = new LineSegments2(lsg, mat);
            seg.matrixAutoUpdate = false;
            seg.matrix.copy(mesh.matrixWorld).multiply(m);
            seg.renderOrder = 999;
            seg.frustumCulled = false;
            container.add(seg);
          }
        }
      }
    }
    if (container.children.length) {
      this._selOutline = { group: container, material: mat, geoms: Array.from(geoCache.values()) };
      this.world.scene.three.add(container);
    } else {
      mat.dispose();
      for (const g of geoCache.values()) g.dispose();
    }
  }

  // Nastaví rozlišení fat-line materiálu na CSS velikost rendereru (nutné pro
  // správnou tloušťku čáry; při změně velikosti okna se aktualizuje).
  _setOutlineResolution(mat) {
    try {
      const r = this.world.renderer && this.world.renderer.three;
      const s = r ? r.getSize(new THREE.Vector2()) : new THREE.Vector2(1, 1);
      mat.resolution.set(s.x || 1, s.y || 1);
    } catch (_) {}
  }
  refreshOutlineResolution() { if (this._selOutline) this._setOutlineResolution(this._selOutline.material); }

  clearSelectionOutline() {
    if (!this._selOutline) return;
    try { this.world.scene.three.remove(this._selOutline.group); } catch (_) {}
    try { this._selOutline.material.dispose(); } catch (_) {}
    for (const g of this._selOutline.geoms || []) { try { g.dispose(); } catch (_) {} }
    this._selOutline = null;
  }

  get STRUCT() { return STRUCT; }

  dispose() {
    try { if (this._onResize && typeof window !== "undefined") window.removeEventListener("resize", this._onResize); } catch (_) {}
    try { this.clearSelectionOutline(); } catch (_) {}
    try { this.clearGhost(); } catch (_) {}
    try { this.components && this.components.dispose(); } catch (_) {}
    this.components = this.world = this.classifier = this.hider = null;
    this.indexer = this.ifcLoader = this.highlighter = this.gridMesh = null;
  }
}
